import { t } from '@/i18n';

import {
  buildCreateMutationErrorOverride,
  buildDeleteMutationErrorOverride,
  buildSaveMutationErrorOverride,
} from './shared';

/**
 * Functions, not constants. A module-level string would resolve once at import and pin the
 * wording to whatever language the app booted in; calling through resolves it when the error is
 * actually shown. Renamed from the former UPPER_SNAKE constants in !1545 — same wording, same call sites, one extra `()`.
 */
export const getTargetGroupDuplicateMessage = (): string => t('modules.campaigns.mutationErrors.targetGroup.duplicate');
export const getTargetGroupCreateCampaignUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.createCampaignUnavailable');
export const getTargetGroupCreateGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.createGeneric');
export const getTargetGroupCopySourceUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.copySourceUnavailable');
export const getTargetGroupCopyGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.copyGeneric');
export const getTargetGroupSaveUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.saveUnavailable');
export const getTargetGroupSaveImmutableMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.saveImmutable');
export const getTargetGroupSaveGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.saveGeneric');
export const getTargetGroupDeleteNotFoundMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.deleteNotFound');
export const getTargetGroupDeleteInUseMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.deleteInUse');
export const getTargetGroupDeleteGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.targetGroup.deleteGeneric');

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getTargetGroupCreateMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorOverride>>
) =>
  buildCreateMutationErrorOverride({
    duplicateMessage: getTargetGroupDuplicateMessage(),
    notFoundMessage: getTargetGroupCreateCampaignUnavailableMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getTargetGroupCopyMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorOverride>>
) =>
  buildCreateMutationErrorOverride({
    duplicateMessage: getTargetGroupDuplicateMessage(),
    notFoundMessage: getTargetGroupCopySourceUnavailableMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getTargetGroupSaveMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildSaveMutationErrorOverride>>
) =>
  buildSaveMutationErrorOverride({
    duplicateMessage: getTargetGroupDuplicateMessage(),
    notFoundMessage: getTargetGroupSaveUnavailableMessage(),
    immutableMessage: getTargetGroupSaveImmutableMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getTargetGroupDeleteMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildDeleteMutationErrorOverride>>
) =>
  buildDeleteMutationErrorOverride({
    notFoundMessage: getTargetGroupDeleteNotFoundMessage(),
    inUseMessage: getTargetGroupDeleteInUseMessage(),
  })(...args);
